<template>
  <div class="page-container work-report-page">
    <div class="card">
      <div class="tabs-header-wrapper">
        <div class="page-title">
          工作报告管理
        </div>
        <div class="action-btn-group">
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="handleAdd"
          >
            新建报告
          </el-button>
        </div>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-select
          v-model="queryForm.reportType"
          placeholder="报告类型"
          clearable
          style="width: 130px"
          @change="handleQuery"
        >
          <el-option
            label="周报"
            value="weekly"
          />
          <el-option
            label="月报"
            value="monthly"
          />
          <el-option
            label="任务明细报告"
            value="task"
          />
        </el-select>
        <el-date-picker
          v-model="queryYearMonth"
          type="month"
          placeholder="选择年月"
          value-format="yyyy-MM"
          style="width: 160px"
          @change="handleYearMonthChange"
        />
        <el-button
          type="primary"
          size="small"
          icon="el-icon-search"
          @click="handleQuery"
        >
          搜索
        </el-button>
        <el-button
          size="small"
          icon="el-icon-refresh"
          @click="handleReset"
        >
          重置
        </el-button>
      </div>

      <!-- 表格 -->
      <div class="table-wrapper">
        <el-table
          v-loading="loading"
          :data="tableData"
          border
          stripe
          style="width: 100%"
          height="100%"
          :header-cell-style="{ textAlign: 'center' }"
          :cell-style="{ textAlign: 'center' }"
        >
          <el-table-column
            label="报告类型"
            width="100"
          >
            <template slot-scope="{ row }">
              <el-tag
                :type="getReportTypeTag(row.reportType)"
                size="small"
              >
                {{ row.reportTypeName || row.reportType }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="期间范围"
            min-width="220"
          >
            <template slot-scope="{ row }">
              {{ getPeriodText(row) }}
            </template>
          </el-table-column>
          <el-table-column
            label="任务维度"
            min-width="210"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              <span v-if="row.reportType === 'task'">{{ getTaskDimensionText(row) }}</span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </template>
          </el-table-column>
          <el-table-column
            label="状态"
            width="100"
          >
            <template slot-scope="{ row }">
              <el-tag
                :type="row.status === 'archived' ? 'success' : 'info'"
                size="small"
              >
                {{ row.statusName || (row.status === 'archived' ? '已归档' : '草稿') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="创建人"
            width="110"
          >
            <template slot-scope="{ row }">
              {{ row.createUserName || row.createUser || '-' }}
            </template>
          </el-table-column>
          <el-table-column
            label="修改时间"
            width="160"
          >
            <template slot-scope="{ row }">
              {{ formatDateTime(row.updateTime) }}
            </template>
          </el-table-column>
          <el-table-column
            label="备注"
            min-width="150"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              <span v-if="row.remark">{{ row.remark }}</span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            width="300"
            fixed="right"
          >
            <template slot-scope="{ row }">
              <el-button
                v-if="row.status === 'draft'"
                type="primary"
                plain
                size="mini"
                @click="handleEdit(row)"
              >
                编辑
              </el-button>
              <el-button
                v-if="row.status === 'draft'"
                type="warning"
                plain
                size="mini"
                :loading="row._previewing"
                @click="handlePreviewFile(row)"
              >
                预览文件
              </el-button>
              <el-button
                v-if="row.status === 'draft'"
                type="success"
                plain
                size="mini"
                :loading="row._archiving"
                @click="handleArchive(row)"
              >
                归档
              </el-button>
              <el-button
                v-if="row.status === 'archived'"
                type="success"
                plain
                size="mini"
                :loading="row._downloading"
                @click="handleDownload(row)"
              >
                下载报告
              </el-button>
              <el-button
                v-if="row.status === 'draft'"
                type="danger"
                plain
                size="mini"
                @click="handleDelete(row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        :current-page="queryForm.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="queryForm.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <!-- 新建/编辑弹窗 -->
    <WorkReportDialog
      v-if="saveDialogVisible"
      :visible.sync="saveDialogVisible"
      :report-id="currentReportId"
      @success="handleSaveSuccess"
    />
  </div>
</template>

<script>
// 服务
import { workReportService } from './work-report.service.js';
// 工具函数
import { formatDateTime, formatDate } from '@/features/common/utils/dateTimeUtil.js';
import { downloadBlobAsFile, downloadFromApiBlobResponse } from '@/features/common/utils/downloadFileUtil.js';
// 组件
import WorkReportDialog from './workReportDialog.vue';

export default {
  name: 'WorkReportList',
  components: { WorkReportDialog },
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,
      queryYearMonth: null,
      queryForm: {
        reportType: '',
        reportYear: null,
        reportMonth: null,
        pageNo: 1,
        pageSize: 10
      },
      saveDialogVisible: false,
      currentReportId: null
    };
  },
  methods: {
    formatDateTime,
    formatDate,
    /** 返回报告类型对应的标签颜色，任务明细报告与原有周报、月报有视觉区分。 */
    getReportTypeTag (type) {
      if (type === 'weekly') return 'primary';
      if (type === 'task') return 'success';
      return 'warning';
    },
    /** 任务明细报告按用户选择的月度/年度显示，避免展示内部使用的打卡区间边界。 */
    getPeriodText (row) {
      if (row.reportType === 'task') {
        if (row.taskReportPeriodType === 'year' && row.taskReportPeriodMonth) {
          return `${row.taskReportPeriodValue || '-'} 年度（${row.taskReportPeriodMonth} 月）`;
        }
        return `${row.taskReportPeriodValue || '-'}${row.taskReportPeriodType === 'year' ? ' 年度' : ' 月度'}`;
      }
      return `${formatDate(row.periodStart)} ~ ${formatDate(row.periodEnd)}`;
    },
    /** 将任务报告归档时保存的筛选维度直观展示在统一报告列表中。 */
    getTaskDimensionText (row) {
      return [row.taskGroupName, row.systemName || '全部消防系统']
        .filter(Boolean).join(' / ');
    },
    getTaskReportFilename (row) {
      const taskGroupName = (row.taskGroupName || '任务工作报告').replace(/\+/g, '').replace(/[\\/:*?"<>|]/g, '_');
      if (row.taskReportPeriodType === 'year' && row.taskReportPeriodMonth) {
        return `${taskGroupName}-${row.taskReportPeriodMonth}月.xlsx`;
      }
      return `${taskGroupName}.xlsx`;
    },
    getDownloadFilename (row) {
      if (row.reportType === 'task') return this.getTaskReportFilename(row);
      const typeName = row.reportType === 'weekly' ? '周报' : '月报';
      const dateStr = row.periodStart ? formatDate(row.periodStart).replace(/-/g, '') : '';
      return `工作${typeName}-${dateStr}-正式报告.docx`;
    },
    /** 加载数据 */
    loadData () {
      this.loading = true;
      workReportService.queryPage(this.queryForm).then(res => {
        const data = res.data;
        this.tableData = (data.list || data.rows || []).map(row => ({
          ...row,
          _previewing: false,
          _archiving: false,
          _downloading: false
        }));
        this.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    /** 查询 */
    handleQuery () {
      this.queryForm.pageNo = 1;
      this.loadData();
    },
    /** 重置 */
    handleReset () {
      this.queryYearMonth = null;
      this.queryForm = {
        reportType: '',
        reportYear: null,
        reportMonth: null,
        pageNo: 1,
        pageSize: 10
      };
      this.loadData();
    },
    /** 处理年月变化 */
    handleYearMonthChange (val) {
      if (val) {
        const parts = val.split('-');
        this.queryForm.reportYear = Number(parts[0]);
        this.queryForm.reportMonth = Number(parts[1]);
      } else {
        this.queryForm.reportYear = null;
        this.queryForm.reportMonth = null;
      }
      this.handleQuery();
    },
    /** 新建报告时在统一弹窗内选择常规或任务明细类型。 */
    handleAdd () {
      this.currentReportId = null;
      this.saveDialogVisible = true;
    },
    /** 编辑 */
    handleEdit (row) {
      this.currentReportId = row.uuid;
      this.saveDialogVisible = true;
    },
    /** 保存成功 */
    handleSaveSuccess () {
      this.saveDialogVisible = false;
      this.loadData();
    },
    /** 预览文件 */
    handlePreviewFile (row) {
      this.$set(row, '_previewing', true);
      workReportService.previewReportFile(row.uuid).then(res => {
        if (row.reportType === 'task') {
          downloadBlobAsFile(res.data, this.getTaskReportFilename(row));
          return;
        }
        const typeName = row.reportType === 'weekly' ? '周报' : '月报';
        const dateStr = row.periodStart ? formatDate(row.periodStart).replace(/-/g, '') : '';
        downloadFromApiBlobResponse(res, `工作${typeName}-${dateStr}预览.docx`);
      }).finally(() => {
        this.$set(row, '_previewing', false);
      });
    },
    /** 归档 */
    handleArchive (row) {
      this.$confirm('归档后报告不可再修改或删除报告，确认归档吗？', '归档锁定', {
        type: 'warning'
      }).then(() => {
        this.$set(row, '_archiving', true);
        workReportService.archive(row.uuid).then(() => {
          this.$message.success('归档成功');
          this.loadData();
        }).finally(() => {
          this.$set(row, '_archiving', false);
        });
      }).catch(() => {});
    },
    /** 下载 */
    handleDownload (row) {
      this.$set(row, '_downloading', true);
      workReportService.getReportFile(row.uuid).then(res => {
        if (row.reportType === 'task') {
          downloadBlobAsFile(res.data, this.getTaskReportFilename(row));
          return;
        }
        downloadFromApiBlobResponse(res, this.getDownloadFilename(row));
      }).finally(() => {
        this.$set(row, '_downloading', false);
      });
    },
    /** 删除 */
    handleDelete (row) {
      this.$confirm('删除后不可恢复，确定删除该报告吗？', '确认删除', {
        type: 'warning'
      }).then(() => {
        workReportService.delete(row.uuid).then(() => {
          this.$message.success('删除成功');
          this.loadData();
        });
      });
    },
    handleSizeChange (size) {
      this.queryForm.pageSize = size;
      this.loadData();
    },
    handlePageChange (page) {
      this.queryForm.pageNo = page;
      this.loadData();
    }
  },
  created () {
    this.loadData();
  }
};
</script>

<style lang="scss" scoped>
.work-report-page {
  padding: 20px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .card {
    flex: 1;
    min-height: 0;
    background: #fff;
    border-radius: 8px;
    padding: 10px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .tabs-header-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      min-height: 48px;
      border-bottom: 1px solid #e4e7ed;
      margin-bottom: 4px;

      .page-title {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
        padding: 12px 0;
      }

      .action-btn-group {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        gap: 8px;
      }
    }

    .search-bar {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;
      margin-bottom: 10px;
    }

    .table-wrapper {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    .pagination {
      padding: 12px 0 4px;
      text-align: right;
      flex-shrink: 0;
    }
  }

  .text-muted {
    color: #c0c4cc;
  }
}
</style>
